import { useState, useEffect } from "react";
import InputField from "../../../components/ui/InputField";
import { Button } from "../../../components/ui/Button";
import { useNavigate, useLocation, Link } from "react-router-dom";
import AuthLayout from "../../../layouts/AuthLayout";
import api from "../../../api";
import { jwtDecode } from "jwt-decode";

const getRoleHomePath = (role) => {
  const r = (role || '').toLowerCase();
  if (r === 'supervisor') return '/supervisor/dashboard';
  return '/dashboard';
};

const LOGIN_SRC_STORAGE_KEY = "nl_labs_login_src";

// Which marketing site sent this visitor here (mindconnect.cloud's training, no longer live,
// pitch vs. mindconnect.in's practice-management pitch) decides which copy
// AuthLayout shows. A fresh ?src= on the URL always wins and is remembered
// for next time; absent that, fall back to whatever was last remembered so
// a bookmarked/typed-in URL still shows the right content, not just the
// training default.
const resolveLoginVariant = (search) => {
  const param = new URLSearchParams(search).get("src");
  if (param === "practice" || param === "training") {
    localStorage.setItem(LOGIN_SRC_STORAGE_KEY, param);
    return param;
  }
  return localStorage.getItem(LOGIN_SRC_STORAGE_KEY) || "training";
};

export default function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState("");
  const [pendingUser, setPendingUser] = useState(null);

  // OTP 2FA step (backend/app/routes/auth/auth.py's /login ->
  // /login/verify-otp) - otpRequired gates a third form alongside the
  // existing login-form/org-picker blocks. preAuthToken is the
  // short-lived token from /login's otp_required response, sent manually
  // on verify/resend calls (see api.js) since no full session exists yet.
  const [otpRequired, setOtpRequired] = useState(false);
  const [otp, setOtp] = useState("");
  const [preAuthToken, setPreAuthToken] = useState(null);
  // Backend computes this ("the phone number ending in 1234" / "the
  // email j***e@nllabs.in") based on OTP_DELIVERY_CHANNEL - the frontend
  // just displays it verbatim rather than assuming SMS and reconstructing
  // phone-specific formatting itself.
  const [otpSentTo, setOtpSentTo] = useState("");
  const [otpSubmitting, setOtpSubmitting] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  // Backend says whether "Send on WhatsApp instead" can be offered (the user
  // has a mobile number and the WhatsApp login-code template is switched on).
  const [whatsappAvailable, setWhatsappAvailable] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const [loginVariant] = useState(() => resolveLoginVariant(location.search));

  // Counts resendCooldown down to 0 once a side effect sets it (after
  // the initial OTP send and after each successful resend) - purely a UI
  // disable timer, the real 60s cooldown is enforced server-side too.
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  // Check if user is already logged in and redirect to dashboard
  useEffect(() => {
    const storedUser = localStorage.getItem("auth");
    if (storedUser) {
      try {
        const authData = JSON.parse(storedUser);
        const token = authData.token;
        
        if (token) {
          try {
            const decoded = jwtDecode(token);
            const now = Date.now() / 1000;
            
            // If token is valid and not expired, redirect to dashboard
            if (decoded.exp > now) {
              // Token is valid, user is logged in
              onLogin(authData);
              navigate(getRoleHomePath(decoded.sub?.role || authData.role));
            } else {
              // Token expired, clear it
              localStorage.removeItem("auth");
              localStorage.removeItem("token");
            }
          } catch (err) {
            // Invalid token, clear it
            console.error("Invalid token in Login check:", err);
            localStorage.removeItem("auth");
            localStorage.removeItem("token");
          }
        }
      } catch (err) {
        // Invalid auth data, clear it
        console.error("Error parsing auth data:", err);
        localStorage.removeItem("auth");
        localStorage.removeItem("token");
      }
    }
  }, [navigate, onLogin]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    try {
      const res = await api.loginUser(email, password);

      if (res.data.otp_required) {
        // Untrusted device - password was correct, but a 2FA code must
        // be verified before any session token is issued.
        setPreAuthToken(res.data.pre_auth_token);
        setOtpSentTo(res.data.otp_sent_to || "");
        setWhatsappAvailable(!!res.data.whatsapp_available);
        setOtp("");
        setOtpRequired(true);
        setResendCooldown(60);
      } else if (res.data.token) {
        // ✅ Single org flow → directly logged in (trusted device, OTP
        // skipped - see backend's device_token check)
        localStorage.setItem("auth", JSON.stringify(res.data));
        localStorage.setItem("forceSubscriptionBannerOnce", "true");
        onLogin(res.data);
        navigate(getRoleHomePath(res.data.role));
      } else if (res.data.organizations) {
        // ✅ Multiple orgs flow → ask user to select org
        setOrganizations(res.data.organizations);
        setPendingUser({
          user_id: res.data.user_id,
          role: res.data.role,
        });
      } else {
        setError("Login failed: No token or organizations received");
      }
    } catch (err) {
      console.error("Login error:", err);
      setError(err.response?.data?.error || "Login failed");
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError("");
    setOtpSubmitting(true);

    try {
      const res = await api.verifyLoginOtp(preAuthToken, otp);
      if (res.data.device_token) {
        // Scoped to this email (see api.js's deviceTrustKey) - not a
        // single shared key, so a different email on this same browser
        // never sends this account's trust token.
        api.storeDeviceTrustToken(email, res.data.device_token);
      }
      localStorage.setItem("auth", JSON.stringify(res.data));
      localStorage.setItem("forceSubscriptionBannerOnce", "true");
      onLogin(res.data);
      navigate(getRoleHomePath(res.data.role));
    } catch (err) {
      console.error("OTP verify error:", err);
      const remaining = err.response?.data?.attempts_remaining;
      setError(
        err.response?.data?.error +
          (remaining !== undefined ? ` (${remaining} attempt${remaining === 1 ? "" : "s"} remaining)` : "") ||
          "Invalid OTP"
      );
    } finally {
      setOtpSubmitting(false);
    }
  };

  const handleResendOtp = async (channel) => {
    if (resendCooldown > 0) return;
    setError("");
    try {
      const res = await api.resendLoginOtp(preAuthToken, channel);
      setOtpSentTo(res.data.otp_sent_to || otpSentTo);
      setWhatsappAvailable(!!res.data.whatsapp_available);
      setResendCooldown(60);
    } catch (err) {
      console.error("OTP resend error:", err);
      setError(err.response?.data?.error || "Failed to resend OTP");
      // Server-enforced cooldown/max-resend errors come back with their
      // own wait_seconds - reflect that in the UI's disable timer too,
      // rather than letting the button go active again immediately.
      if (err.response?.data?.wait_seconds) {
        setResendCooldown(err.response.data.wait_seconds);
      }
    }
  };

  // Returns to Step 1 (email/password) - there was previously no way back
  // from the OTP screen at all once the pre-auth token expired or OTP
  // verification kept failing, leaving the user stuck. Password is
  // cleared (re-enter it deliberately) but email is kept, since retyping
  // that is just friction with no security benefit.
  const handleBackToLogin = () => {
    setOtpRequired(false);
    setPreAuthToken(null);
    setOtp("");
    setOtpSentTo("");
    setWhatsappAvailable(false);
    setPassword("");
    setError("");
    setResendCooldown(0);
  };

  const handleSelectOrg = async () => {
    if (!selectedOrg || !pendingUser) {
      setError("Please select an organization");
      return;
    }

    try {
      const res = await api.selectOrg(
        pendingUser.user_id,
        pendingUser.role,
        selectedOrg
      );
      console.log("Org selection response:", res.data);

      if (res.data.token) {
        localStorage.setItem("auth", JSON.stringify(res.data));
        localStorage.setItem("forceSubscriptionBannerOnce", "true");
        onLogin(res.data);
        navigate(getRoleHomePath(res.data.role));
      } else {
        setError("Failed to complete login");
      }
    } catch (err) {
      console.error("Select org error:", err);
      setError(err.response?.data?.error || "Login failed");
    }
  };

  return (
    <AuthLayout variant={loginVariant}>
      <h2 className="text-2xl font-bold mb-6 text-center">Welcome Back</h2>
      {error && <p className="text-red-500 mb-4">{error}</p>}

      {/* Step 1: Login form */}
      {!otpRequired && (
        <form onSubmit={handleLogin}>
          <InputField
            label="Email ID"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-label="Email Input"
          />
          <InputField
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-label="Password Input"
          />
          <div className="text-right mb-4 -mt-2">
            <Link to="/forgot-password" className="text-sm font-medium text-primary-600 hover:text-primary-700">
              Forgot password?
            </Link>
          </div>
          <Button
            type="submit"
            text="Login"
            value="Login"
            data-label="Login Submit"
            data-gtm-payload={JSON.stringify({ user_email: email })}
          >
            Login
          </Button>
        </form>
      )}

      {/* Step 2: OTP verification - only reached once email+password are
          already correct but this device hasn't been trusted in the
          last 7 days. */}
      {otpRequired && (
        <form onSubmit={handleVerifyOtp}>
          <p className="text-sm text-gray-600 mb-4">
            We've sent a 6-digit code to{" "}
            <span className="font-semibold">{otpSentTo || "your registered contact"}</span>.
          </p>
          <InputField
            label="OTP"
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
            data-label="OTP Input"
          />
          <div className="flex flex-col items-center gap-3 mt-2">
            <Button
              type="submit"
              text="Verify"
              value="Verify"
              data-label="OTP Submit"
              disabled={otpSubmitting || otp.length !== 6}
              className="w-full"
            >
              {otpSubmitting ? "Verifying..." : "Verify"}
            </Button>
            <button
              type="button"
              onClick={() => handleResendOtp()}
              disabled={resendCooldown > 0}
              className="text-sm font-medium text-primary-600 hover:text-primary-700 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {resendCooldown > 0 ? `Resend OTP in ${resendCooldown}s` : "Resend OTP"}
            </button>
            {whatsappAvailable && (
              <button
                type="button"
                onClick={() => handleResendOtp("whatsapp")}
                disabled={resendCooldown > 0}
                data-label="OTP Resend WhatsApp"
                className="text-sm font-medium text-emerald-700 hover:text-emerald-800 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                Send on WhatsApp instead
              </button>
            )}
            <button
              type="button"
              onClick={handleBackToLogin}
              className="text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
            >
              ← Back to Login
            </button>
          </div>
        </form>
      )}

      {/* Footer */}

    </AuthLayout>
  );
}
